/**
 * topPlayers — per-team Top 3 ranking (EPIC-107, spec D4). Pure.
 *
 * score = 6*goals + 4*assists + 2*shotsOnTarget + 0.15*passesGood
 *       + 1*duelsWon + 1*(recoveries + interceptions) + 1.5*saves
 *       - 0.5*foulsCommitted - 1*yellowCards - 3*redCards - 3*ownGoals
 *
 * The weights live in ONE exported constant; changing a weight is a
 * one-line change here (and `topPlayers.test.ts` pins every value).
 * Ties break by touches, then minutes played, then jersey number ascending.
 * Players with zero touches are excluded; fewer than `limit` eligible
 * players returns only those (no placeholders).
 */
import type { PlayerStats } from "../hooks/usePlayerStats";

export const TOP_PLAYER_SCORE_WEIGHTS = {
  goals: 6,
  assists: 4,
  shotsOnTarget: 2,
  passesGood: 0.15,
  duelsWon: 1,
  recoveries: 1,
  interceptions: 1,
  saves: 1.5,
  foulsCommitted: -0.5,
  yellowCards: -1,
  redCards: -3,
  ownGoals: -3,
} as const;

export type TopPlayerStatKey = keyof typeof TOP_PLAYER_SCORE_WEIGHTS;

const STAT_KEYS = Object.keys(TOP_PLAYER_SCORE_WEIGHTS) as TopPlayerStatKey[];

export interface TopPlayerContribution {
  key: TopPlayerStatKey;
  count: number;
}

export interface TopPlayerRow {
  rank: number;
  playerId: string;
  playerName: string;
  jerseyNumber: number;
  minutes: number;
  touches: number;
  /** The two strongest positive contributions (weight * count), highest first. */
  contributions: TopPlayerContribution[];
}

const statCount = (p: PlayerStats, key: TopPlayerStatKey): number =>
  Number((p as unknown as Record<string, number | undefined>)[key] ?? 0);

/** Exported for the weights test; rounded to kill float noise (0.15 steps). */
export const computeTopPlayerScore = (p: PlayerStats): number => {
  let score = 0;
  for (const key of STAT_KEYS) {
    score += TOP_PLAYER_SCORE_WEIGHTS[key] * statCount(p, key);
  }
  return Math.round(score * 1e6) / 1e6;
};

const strongestContributions = (p: PlayerStats): TopPlayerContribution[] =>
  STAT_KEYS.filter(
    (key) => TOP_PLAYER_SCORE_WEIGHTS[key] > 0 && statCount(p, key) > 0,
  )
    .map((key, order) => ({
      key,
      count: statCount(p, key),
      value:
        Math.round(TOP_PLAYER_SCORE_WEIGHTS[key] * statCount(p, key) * 1e6) /
        1e6,
      order,
    }))
    .sort((a, b) => b.value - a.value || a.order - b.order)
    .slice(0, 2)
    .map(({ key, count }) => ({ key, count }));

export function rankTopPlayers(
  stats: PlayerStats[],
  minutesByPlayer: Record<string, number>,
  side: "home" | "away",
  limit = 3,
): TopPlayerRow[] {
  return stats
    .filter((p) => p.teamSide === side && p.touches > 0)
    .map((p) => ({
      p,
      score: computeTopPlayerScore(p),
      minutes: minutesByPlayer[p.playerId] ?? 0,
    }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.p.touches - a.p.touches ||
        b.minutes - a.minutes ||
        a.p.jerseyNumber - b.p.jerseyNumber,
    )
    .slice(0, limit)
    .map(({ p, minutes }, i) => ({
      rank: i + 1,
      playerId: p.playerId,
      playerName: p.playerName,
      jerseyNumber: p.jerseyNumber,
      minutes,
      touches: p.touches,
      contributions: strongestContributions(p),
    }));
}
